import { IonSpinner } from '@ionic/react';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React from 'react';

import { UiButtonBase } from '@/components/ui/ButtonBase';
import { UiTypography } from '@/components/ui/Typography';
import { useAppModals } from '@/hooks/useAppModals';
import { usePollingUser } from '@/hooks/usePollingUser';
import { amplitudeModule } from '@/modules/amplitude';
import { fireConfetti } from '@/modules/confetti';
import { EV_EM_SUBSCRIPTION_KEY, eventEmitter } from '@/modules/event-emitter';
import { FacebookModule } from '@/modules/facebook';
import { StripeV2CoinsPrice } from '@/services/stripe/dto';
import { canLogEvents } from '@/utils/canLogEvents';

import './StripeCoinsForm.scss';

export const StripeCoinsForm: React.FC<{
  clientSecret: string;
  product: StripeV2CoinsPrice;
  disabled: boolean;
  onCoinsIncrease?: () => void;
}> = ({ clientSecret, product, disabled, onCoinsIncrease }) => {
  const { startPolling, stopPolling } = usePollingUser();

  const { hide } = useAppModals('buyCoinsPaywall');

  const [stripePurchase, setStripePurchase] = React.useState({
    error: '',
    success: false,
    isSubmitting: false,
  });

  const elements = useElements();
  const stripe = useStripe();

  const disablePurchase = !elements || !stripe;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (disablePurchase) {
      return;
    }

    setStripePurchase((prev) => ({ ...prev, isSubmitting: true }));

    await elements.submit();

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      clientSecret,
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (stripeError) {
      let message = stripeError.message ?? 'Unknown error';
      const isApplePayError = message.includes('Unable to show Apple Pay');

      if (isApplePayError) {
        message = 'Something went wrong with Apple Pay.';
      }

      setStripePurchase({
        error: message,
        success: false,
        isSubmitting: false,
      });
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      startPolling();
      setStripePurchase(() => ({
        error: '',
        success: true,
        isSubmitting: false,
      }));

      if (canLogEvents()) {
        amplitudeModule.logEvent('web_purchase', {
          plan: product.id,
        });
        FacebookModule.trackEvent('Purchase', {
          value: product.price,
          currency: 'USD',
        });

        // HilltopAds postback
        window.sendHilltopAdsPostback && window.sendHilltopAdsPostback();

        // TrafficStars postback
        window.sendTrafficStarsPostback && window.sendTrafficStarsPostback();
      }
    }
  };

  React.useEffect(() => {
    const subscription = eventEmitter.subscribe(
      EV_EM_SUBSCRIPTION_KEY.COINS_BALANCE_INCREASE,
      () => {
        stopPolling();
        setStripePurchase((prev) => ({ ...prev, isSubmitting: false }));
        fireConfetti();
        hide();
        onCoinsIncrease?.();
      },
    );
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disable =
    stripePurchase.isSubmitting || stripePurchase.success || disabled;

  return (
    <form onSubmit={handleSubmit} id="stripe-form">
      <PaymentElement
        options={{
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
          paymentMethodOrder: [
            'card',
            'us_bank_account',
            'google_pay',
            'cashapp',
            'apple_pay',
            'samsung_pay',
            'paypal',
          ],
          layout: 'tabs',
        }}
      />
      {stripePurchase.error && (
        <UiTypography className="stripe-form-message" color="danger">
          {stripePurchase.error}
        </UiTypography>
      )}
      {stripePurchase.success && (
        <UiTypography className="stripe-form-message">
          We are processing your payment...
          <IonSpinner name="circles" />
        </UiTypography>
      )}
      <UiButtonBase
        type="submit"
        pending={stripePurchase.isSubmitting}
        disabled={disable}
        className="stripe-form-btn-submit"
        expand="full"
      >
        Buy coins
      </UiButtonBase>
    </form>
  );
};
